import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  AlertTriangle, ChevronDown, ChevronUp, Coins, Dices, Disc3, Edit3, Eye,
  EyeOff, History, Info, Layers, Loader2, Percent, Plus, RefreshCw,
  Save, Search, Sparkles, Target, Trash2, TrendingDown, X,
} from 'lucide-react';
import client from '../lib/api';
import { useToast } from '../hooks/use-toast';
import ConfirmDialog from './components/ConfirmDialog';
import { AdminEmpty, AdminError, AdminSkeleton } from './components/PageState';
import PageSizeSelect from './components/PageSizeSelect';
import { pageWindow, readPageSize, writePageSize }
  from './lib/pagination';
import { reorderSegments, segmentChance, segmentStats } from './lib/wheelStats';
import {
  WHEEL_RADIUS, contrastTextColor, labelFontSize, polarPoint,
  radialLabelLayout, segmentPath, shortLabel,
} from '../lib/wheelGeometry';
import './wheelSettings.css';

const REWARD_LABELS = {
  cash: 'Bakiye',
  bonus: 'Bonus Bakiye',
  freespin: 'Freespin',
  none: 'Boş Dilim',
};

const REWARD_BADGES = {
  cash: 'badge-green',
  bonus: 'badge-yellow',
  freespin: 'badge-blue',
  none: 'badge-gray',
};

// Freespin oyuncu bazlı tanımlanabilen sağlayıcılardan verilir: Gold Palace
// (freeround) veya ScorpioPlay (bonus-call). Drakon kampanya modeliyle
// verildiği için buraya girmez (bkz. services/game_access.PROVIDER_META).
const FREESPIN_PROVIDERS = ['goldpalace', 'scorpio'];

const PALETTE = [
  '#22d3ee', '#a78bfa', '#34d399', '#fbbf24', '#f472b6',
  '#ef4444', '#60a5fa', '#334155',
];

// Çevirme kayıtları sayfalanır: uç 200 satıra kadar veriyor ama tek ekranda
// o kadar satır okunmuyor ve liste sayfanın altını süresiz uzatıyordu.
// Yalnızca varsayılan: boyutu yönetici seçer (bkz. lib/pagination).
const SPIN_PAGE_SIZE = 25;

const emptySegment = {
  label: '',
  // Varsayılan ödül doğrudan ana bakiyeye yazılır; çevrim şartı isteyen
  // dilimler bilinçli olarak "bonus" türüne çevrilir.
  reward_type: 'cash',
  amount: 0,
  wager_multiplier: 0,
  game_id: '',
  spin_count: 0,
  weight: 10,
  color: '#22d3ee',
  text_color: '#0b1220',
  active: true,
  order: 0,
};

const money = (value) => new Intl.NumberFormat('tr-TR', {
  style: 'currency', currency: 'TRY', maximumFractionDigits: 2,
}).format(Number(value || 0));

const percent = (ratio) => `%${(Number(ratio || 0) * 100).toFixed(1)}`;

const formatDate = (value) => (value ? new Date(value).toLocaleString('tr-TR') : '-');

// Kuralı yöneticiye kendi cümlesiyle geri okutur: iki seçim alanı yan yana
// durduğunda "hangi kombinasyon ne demek" ekranda görünmüyordu.
const depositRuleText = (settings) => {
  const amount = Number(settings?.wheel_min_deposit || 0);
  if (amount <= 0) return 'Yatırım şartı kapalı — her üye çevirebilir.';
  const window = settings?.wheel_min_deposit_window || 'total';
  if (window === 'daily') {
    return `Bugün (00:00–23:59) en az ${money(amount)} yatırmış üyeler çevirebilir; `
      + 'sayaç her gece sıfırlanır.';
  }
  if (window === 'rolling') {
    const hours = Number(settings?.wheel_min_deposit_hours || 0);
    return `Son ${hours} saat içinde en az ${money(amount)} yatırmış üyeler çevirebilir.`;
  }
  return `Ömür boyu toplam yatırımı en az ${money(amount)} olan üyeler çevirebilir.`;
};

const cooldownRuleText = (settings) => {
  if ((settings?.wheel_cooldown_mode || 'rolling') === 'daily') {
    return 'Her üye günde bir kez çevirir; hak gece 00:00\'da yenilenir.';
  }
  const hours = Number(settings?.wheel_cooldown_hours || 0);
  return hours > 0
    ? `Aynı üye ${hours} saatte bir çevirebilir (son çevirmesinden itibaren).`
    : 'Çevirme sıklığında sınır yok.';
};

const rewardSummary = (segment) => {
  if (segment.reward_type === 'none') return 'Ödül yok';
  if (segment.reward_type === 'freespin') {
    return `${segment.spin_count || 0} spin × ${money(segment.amount)}`;
  }
  if (segment.reward_type === 'bonus' && Number(segment.wager_multiplier || 0) > 0) {
    return `${money(segment.amount)} · çevrim x${Number(segment.wager_multiplier)}`;
  }
  return `${money(segment.amount)} · çevrimsiz`;
};

/**
 * Çarkın canlı önizlemesi.
 *
 * Geometri oyuncu sayfasıyla **ortak modülden** gelir (lib/wheelGeometry.js);
 * ayrı yazılsaydı yöneticinin panelde gördüğü dizilim ile oyuncunun gördüğü
 * çark ayrışırdı. Yalnızca **aktif** dilimler çizilir: çarkta görünen küme
 * budur (services/wheel.py `active_segments`).
 */
export const WheelPreview = ({ segments = [], highlight = '', onPick }) => {
  const visible = segments.filter((segment) => segment.active);

  if (visible.length === 0) {
    return (
      <div className="wh-preview-disc">
        <div className="wh-preview-empty">
          Aktif dilim yok — çark oyuncuya boş görünür.
        </div>
      </div>
    );
  }

  return (
    <div className="wh-preview-disc">
      <svg viewBox="0 0 200 200" role="img" aria-label="Çark önizlemesi">
        <defs>
          <radialGradient id="wh-preview-depth" cx="50%" cy="50%" r="50%">
            <stop offset="55%" stopColor="rgba(0,0,0,0)" />
            <stop offset="100%" stopColor="rgba(0,0,0,0.35)" />
          </radialGradient>
        </defs>

        {visible.map((segment, index) => {
          const sweep = 360 / visible.length;
          const center = index * sweep + sweep / 2;
          const [textX, textY] = polarPoint(center, WHEEL_RADIUS * 0.9);
          const { angle, anchor } = radialLabelLayout(center);
          const dimmed = Boolean(highlight) && highlight !== segment.id;
          return (
            <g
              key={segment.id}
              className={`wh-preview-slice${dimmed ? ' is-dimmed' : ''}`}
              onClick={() => onPick?.(segment)}
            >
              <title>{`${segment.label} — ${REWARD_LABELS[segment.reward_type] || ''}`}</title>
              <path
                d={segmentPath(index, visible.length)}
                fill={segment.color}
                stroke="rgba(2,6,23,0.45)"
                strokeWidth="0.6"
              />
              <text
                x={textX}
                y={textY}
                fill={segment.text_color}
                fontSize={labelFontSize(visible.length)}
                fontWeight="800"
                textAnchor={anchor}
                dominantBaseline="middle"
                transform={`rotate(${angle}, ${textX}, ${textY})`}
              >
                {shortLabel(segment.label, visible.length)}
              </text>
            </g>
          );
        })}

        <circle cx="100" cy="100" r={WHEEL_RADIUS} fill="url(#wh-preview-depth)" />
        <circle
          cx="100" cy="100" r={WHEEL_RADIUS + 5}
          fill="none" stroke="var(--accent-primary)" strokeWidth="7"
        />
        <circle cx="100" cy="100" r="16" fill="var(--bg-surface)" />
        <circle
          cx="100" cy="100" r="16"
          fill="none" stroke="var(--accent-primary)" strokeWidth="2.5"
        />
      </svg>
      <Target className="wh-preview-pin" size={18} aria-hidden="true" />
    </div>
  );
};

const Field = ({ label, wide = false, children }) => (
  <label className={`wh-field${wide ? ' is-wide' : ''}`}>
    <span>{label}</span>
    {children}
  </label>
);

const WheelSettings = () => {
  const [settings, setSettings] = useState(null);
  const [segments, setSegments] = useState([]);
  const [games, setGames] = useState([]);
  const [spins, setSpins] = useState({ items: [], total: 0, total_amount: 0 });
  // Sekme ve özet kartı **aramadan bağımsız** toplamı gösterir: filtreli
  // sayıyı oraya yazmak "toplam çevirme" rakamını arama kutusuna bağlardı.
  const [spinTotalAll, setSpinTotalAll] = useState(0);
  const [spinSearch, setSpinSearch] = useState('');
  const [spinPage, setSpinPage] = useState(0);
  const [spinSize, setSpinSize] = useState(
    () => readPageSize('wheel-spins', SPIN_PAGE_SIZE));
  const [spinsBusy, setSpinsBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState('segments');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptySegment);
  const [highlight, setHighlight] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const searchTimer = useRef(null);
  // Güncel sayfa boyutu: `loadSpins` bağımlılığına eklenseydi boyut
  // değişiminde liste parametresiz yeniden yüklenir ve arama terimi
  // sessizce sıfırlanırdı.
  const spinSizeRef = useRef(spinSize);
  spinSizeRef.current = spinSize;
  const { toast } = useToast();

  const freespinGames = useMemo(
    () => games.filter(
      (game) => FREESPIN_PROVIDERS.includes(game.api_provider) && game.active),
    [games],
  );

  const loadSpins = useCallback(async (options = {}) => {
    const { username = '', page = 0, limit = spinSizeRef.current } = options;
    setSpinsBusy(true);
    try {
      const { data } = await client.get('/admin/wheel/spins', {
        params: {
          username: username.trim(),
          skip: page * limit,
          limit,
        },
      });
      const total = Number(data.total || 0);
      setSpins({
        items: data.items || [],
        total,
        total_amount: Number(data.total_amount || 0),
      });
      if (!username.trim()) setSpinTotalAll(total);
    } catch (requestError) {
      toast({
        title: 'Çevirme kayıtları alınamadı',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setSpinsBusy(false);
    }
  }, [toast]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [
        { data: settingsData },
        { data: segmentData },
        { data: gameData },
      ] = await Promise.all([
        client.get('/admin/wheel/settings'),
        client.get('/admin/wheel/segments'),
        // Oyun kataloğu games.read ister; yetkisi olmayan rolde sayfa yine açılmalı.
        client.get('/admin/games').catch(() => ({ data: {} })),
      ]);
      setSettings(settingsData);
      setSegments(segmentData.items || []);
      setGames(gameData.items || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Şans çarkı ayarları alınamadı');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSpins(); }, [loadSpins]);

  // Arama her harfte istek atmaz: kayıt listesi sayfalıdır ve tuşa basıldıkça
  // sorgulanan bir liste tek worker'lı backend'de gereksiz yük üretir.
  const onSearch = (value) => {
    setSpinSearch(value);
    setSpinPage(0);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(
      () => loadSpins({ username: value, page: 0 }), 400);
  };

  useEffect(() => () => clearTimeout(searchTimer.current), []);

  const goPage = (page) => {
    setSpinPage(page);
    loadSpins({ username: spinSearch, page });
  };

  const stats = useMemo(() => segmentStats(segments), [segments]);
  const pageCount = Math.max(1, Math.ceil(spins.total / spinSize));

  const saveSettings = async (overrides = {}) => {
    setSavingSettings(true);
    try {
      const payload = {
        ...settings,
        ...overrides,
        wheel_cooldown_hours: Number(settings.wheel_cooldown_hours || 0),
        wheel_cooldown_mode: settings.wheel_cooldown_mode || 'rolling',
        wheel_min_deposit: Number(settings.wheel_min_deposit || 0),
        wheel_min_deposit_window: settings.wheel_min_deposit_window || 'total',
        // Kayan pencerede saat zorunludur; boş bırakılmış bir seçim şartı
        // sessizce "ömür boyu"ya düşürmesin diye 24'e tamamlanır.
        wheel_min_deposit_hours: (
          (settings.wheel_min_deposit_window || 'total') === 'rolling'
            ? Number(settings.wheel_min_deposit_hours || 0) || 24
            : Number(settings.wheel_min_deposit_hours || 0)
        ),
      };
      const { data } = await client.patch('/admin/wheel/settings', payload);
      setSettings(data);
      toast({ title: 'Çark ayarları kaydedildi' });
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
      load();
    } finally {
      setSavingSettings(false);
    }
  };

  const openNew = () => {
    const color = PALETTE[segments.length % PALETTE.length];
    setForm({
      ...emptySegment,
      order: segments.length,
      color,
      text_color: contrastTextColor(color),
    });
    setModal({ mode: 'new' });
  };

  const openEdit = (segment) => {
    setForm({ ...emptySegment, ...segment, game_id: segment.game_id || '' });
    setModal({ mode: 'edit', id: segment.id });
  };

  // Renk değişince yazı rengi de yeniden seçilir: koyu bir dilimde koyu yazı
  // çarkta hiç okunmuyor ve arıza ancak oyuncu sayfası açılınca görülüyordu.
  const pickColor = (color) => setForm((previous) => ({
    ...previous, color, text_color: contrastTextColor(color),
  }));

  const saveSegment = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        label: form.label.trim(),
        amount: Number(form.amount || 0),
        wager_multiplier: Number(form.wager_multiplier || 0),
        spin_count: Number(form.spin_count || 0),
        weight: Number(form.weight || 0),
        order: Number(form.order || 0),
        game_id: form.reward_type === 'freespin' ? form.game_id : null,
      };
      if (modal.mode === 'new') await client.post('/admin/wheel/segments', payload);
      else await client.patch(`/admin/wheel/segments/${modal.id}`, payload);
      toast({ title: 'Dilim kaydedildi' });
      setModal(null);
      await load();
    } catch (requestError) {
      toast({
        title: 'Kaydedilemedi',
        description: requestError.response?.data?.detail || 'Beklenmeyen bir hata oluştu',
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleSegment = async (segment) => {
    try {
      await client.patch(`/admin/wheel/segments/${segment.id}`, { active: !segment.active });
      await load();
    } catch (requestError) {
      toast({
        title: 'Durum güncellenemedi',
        description: requestError.response?.data?.detail,
      });
    }
  };

  const moveSegment = async (index, direction) => {
    const { items, changed } = reorderSegments(segments, index, direction);
    if (changed.length === 0) return;
    // Liste önce yerelde taşınır: sunucu yanıtını beklemek dilim sırasını
    // düzenlerken her tıklamada ekranı dondururdu.
    setSegments(items);
    setOrdering(true);
    try {
      await Promise.all(changed.map(
        (entry) => client.patch(`/admin/wheel/segments/${entry.id}`, {
          order: entry.order,
        }),
      ));
    } catch (requestError) {
      toast({
        title: 'Sıra kaydedilemedi',
        description: requestError.response?.data?.detail,
      });
    } finally {
      setOrdering(false);
      await load();
    }
  };

  const removeSegment = async (segment) => {
    setBusy(true);
    try {
      const { data } = await client.delete(`/admin/wheel/segments/${segment.id}`);
      if (data.auto_disabled) {
        toast({
          title: 'Dilim silindi',
          description: 'Ağırlıklı dilim kalmadığı için çark otomatik kapatıldı.',
        });
      } else {
        toast({ title: 'Dilim silindi' });
      }
      setConfirm(null);
      await load();
    } catch (requestError) {
      toast({ title: 'Silinemedi', description: requestError.response?.data?.detail });
    } finally {
      setBusy(false);
    }
  };

  const seedDefaults = async () => {
    try {
      await client.post('/admin/wheel/segments/defaults');
      toast({ title: 'Örnek dilimler yüklendi' });
      await load();
    } catch (requestError) {
      toast({ title: 'Yüklenemedi', description: requestError.response?.data?.detail });
    }
  };

  if (loading) return <AdminSkeleton rows={7} label="Şans çarkı yükleniyor" />;
  if (error) return <AdminError message={error} onRetry={load} />;

  const cooldownMode = settings.wheel_cooldown_mode || 'rolling';
  const depositWindow = settings.wheel_min_deposit_window || 'total';

  return (
    <div className="wh-page">
      <header className="wh-hero">
        <span className="wh-hero-mark"><Disc3 size={20} /></span>
        <div className="wh-hero-copy">
          <h1>Şans Çarkı</h1>
          <p>
            Dilimleri, kazanma olasılıklarını ve katılım kurallarını yönetin.
            Kazanan dilim her zaman sunucuda seçilir.
          </p>
        </div>
        <div className="wh-hero-actions">
          <button type="button" onClick={load} className="admin-btn-secondary">
            <RefreshCw size={14} /> Yenile
          </button>
          <button type="button" onClick={openNew} className="admin-btn-primary">
            <Plus size={14} /> Yeni Dilim
          </button>
        </div>
      </header>

      <div className={`wh-power${settings.wheel_enabled ? ' is-on' : ''}`}>
        <span className="wh-power-dot"><Disc3 size={18} /></span>
        <div className="wh-power-copy">
          <strong>{settings.wheel_enabled ? 'Çark yayında' : 'Çark kapalı'}</strong>
          <span>
            {settings.wheel_enabled
              ? 'Üyeler /cark sayfasından çevirebilir ve menüdeki bağlantı görünür.'
              : 'Kapalıyken /cark sayfası “şu anda kapalı” bilgisi gösterir ve menüdeki bağlantı gizlenir.'}
          </span>
        </div>
        <button
          type="button"
          onClick={() => saveSettings({ wheel_enabled: !settings.wheel_enabled })}
          disabled={savingSettings}
          className={`wh-switch${settings.wheel_enabled ? ' is-on' : ''}`}
        >
          <span className="wh-switch-track" aria-hidden="true" />
          {settings.wheel_enabled ? 'Açık' : 'Kapalı'}
        </button>
      </div>

      {segments.length === 1 && (
        // Tek dilimli çark teknik olarak çalışır ama her çevirmede aynı ödülü
        // verir; oyuncuya çark gibi görünmez.
        <div className="wh-note is-warn">
          <AlertTriangle size={15} />
          <div>
            Çarkta <b>tek dilim</b> var — her çevirme aynı ödülü verir. En az 3-4
            dilim tanımlarsanız çark gerçek bir şans oyunu gibi görünür.
          </div>
        </div>
      )}

      {stats.deadWeight > 0 && (
        <div className="wh-note is-warn">
          <AlertTriangle size={15} />
          <div>
            <b>{stats.deadWeight} aktif dilimin ağırlığı sıfır</b> — çarkta
            görünür ama asla gelmez. Kazanılmasını istiyorsanız ağırlığını
            sıfırdan büyük yapın.
          </div>
        </div>
      )}

      <div className="wh-stats">
        <div className="wh-stat">
          <span className="wh-stat-icon accent"><Layers size={17} /></span>
          <div className="wh-stat-body">
            <span className="wh-stat-value">{`${stats.activeCount} / ${stats.total}`}</span>
            <span className="wh-stat-label">Aktif dilim / toplam</span>
          </div>
        </div>
        <div className="wh-stat">
          <span className="wh-stat-icon blue"><Percent size={17} /></span>
          <div className="wh-stat-body">
            <span className="wh-stat-value">{stats.totalWeight}</span>
            <span className="wh-stat-label">Toplam ağırlık (olasılık paydası)</span>
          </div>
        </div>
        <div className="wh-stat">
          <span className="wh-stat-icon amber"><TrendingDown size={17} /></span>
          <div className="wh-stat-body">
            <span className="wh-stat-value">{money(stats.expectedCost)}</span>
            <span className="wh-stat-label">Çevirme başına ortalama ödül</span>
          </div>
        </div>
        <div className="wh-stat">
          <span className="wh-stat-icon"><Target size={17} /></span>
          <div className="wh-stat-body">
            <span className="wh-stat-value">{percent(stats.blankChance)}</span>
            <span className="wh-stat-label">Boş dilime düşme olasılığı</span>
          </div>
        </div>
        <div className="wh-stat">
          <span className="wh-stat-icon green"><History size={17} /></span>
          <div className="wh-stat-body">
            <span className="wh-stat-value">{spinTotalAll}</span>
            <span className="wh-stat-label">Toplam çevirme</span>
          </div>
        </div>
      </div>

      <div className="wh-cols">
        <section className="wh-card">
          <div className="wh-card-head">
            <Eye size={15} />
            <h2>Canlı Önizleme</h2>
            <p>
              Oyuncunun göreceği dizilim. Bir dilime tıklayarak doğrudan
              düzenleyebilirsiniz.
            </p>
          </div>
          <WheelPreview
            segments={segments}
            highlight={highlight}
            onPick={openEdit}
          />
          <p className="wh-preview-caption">
            Yalnızca aktif dilimler çizilir; sıra listedeki sıralamadır.
            {stats.expectedCost > 0
              && ` Ortalama ödül: ${money(stats.expectedCost)} / çevirme.`}
          </p>
        </section>

        <section className="wh-card">
          <div className="wh-card-head">
            <Sparkles size={15} />
            <h2>Sayfa ve Katılım Kuralları</h2>
            <p>
              Başlık ve açıklama çark sayfasında görünür; kurallar kimin ne
              sıklıkla çevirebileceğini belirler.
            </p>
          </div>

          <div className="wh-fields">
            <Field label="Sayfa Başlığı">
              <input
                value={settings.wheel_title || ''}
                onChange={(event) => setSettings({
                  ...settings, wheel_title: event.target.value,
                })}
                className="admin-input"
                maxLength={128}
              />
            </Field>
            <Field label="Çevirme Hakkı">
              <select
                value={cooldownMode}
                onChange={(event) => setSettings({
                  ...settings, wheel_cooldown_mode: event.target.value,
                })}
                className="admin-input"
              >
                <option value="rolling">Son çevirmeden itibaren (saat)</option>
                <option value="daily">Günde 1 hak (gece 00:00&apos;da yenilenir)</option>
              </select>
            </Field>
            {cooldownMode === 'rolling' && (
              <Field label="Çevirme Aralığı (saat, 0 = sınırsız)">
                <input
                  type="number"
                  min="0"
                  value={settings.wheel_cooldown_hours ?? 24}
                  onChange={(event) => setSettings({
                    ...settings, wheel_cooldown_hours: event.target.value,
                  })}
                  className="admin-input"
                />
              </Field>
            )}
            <Field label="Minimum Yatırım (0 = şart yok)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={settings.wheel_min_deposit ?? 0}
                onChange={(event) => setSettings({
                  ...settings, wheel_min_deposit: event.target.value,
                })}
                className="admin-input"
              />
            </Field>
            <Field label="Yatırım Penceresi">
              <select
                value={depositWindow}
                onChange={(event) => setSettings({
                  ...settings, wheel_min_deposit_window: event.target.value,
                })}
                className="admin-input"
              >
                <option value="total">Tüm zamanlar (ömür boyu toplam)</option>
                <option value="daily">Bugün (00:00–23:59)</option>
                <option value="rolling">Son N saat</option>
              </select>
            </Field>
            {depositWindow === 'rolling' && (
              <Field label="Pencere Uzunluğu (saat)">
                <input
                  type="number"
                  min="1"
                  value={settings.wheel_min_deposit_hours ?? 24}
                  onChange={(event) => setSettings({
                    ...settings, wheel_min_deposit_hours: event.target.value,
                  })}
                  className="admin-input"
                />
              </Field>
            )}

            <div className="wh-rule-lines">
              <p><Info size={13} /> {cooldownRuleText(settings)}</p>
              <p><Info size={13} /> {depositRuleText(settings)}</p>
              <p>
                <Info size={13} />
                Çarkta KYC şartı yoktur; doğrulanmamış üye de çevirebilir.
              </p>
            </div>

            <Field label="Açıklama (çark sayfasında görünür)" wide>
              <textarea
                rows={3}
                value={settings.wheel_description || ''}
                onChange={(event) => setSettings({
                  ...settings, wheel_description: event.target.value,
                })}
                className="admin-input"
              />
            </Field>

            <div className="wh-form-actions">
              <button
                type="button"
                onClick={() => saveSettings()}
                disabled={savingSettings}
                className="admin-btn-primary"
              >
                {savingSettings
                  ? <Loader2 size={14} className="animate-spin" />
                  : <Save size={14} />}
                Ayarları Kaydet
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className="wh-tabs">
        <button
          type="button"
          onClick={() => setView('segments')}
          className={`admin-tab ${view === 'segments' ? 'active' : ''}`}
        >
          <Layers size={13} /> Dilimler ({segments.length})
        </button>
        <button
          type="button"
          onClick={() => setView('spins')}
          className={`admin-tab ${view === 'spins' ? 'active' : ''}`}
        >
          <History size={13} /> Çevirme Kayıtları ({spinTotalAll})
        </button>
      </div>

      {view === 'segments' ? (
        <section className="wh-card">
          {segments.length === 0 ? (
            <AdminEmpty
              title="Çarkta henüz dilim yok"
              description="Dilimleri tek tek ekleyebilir ya da hazır bir set ile başlayıp düzenleyebilirsiniz."
              action={(
                <button type="button" onClick={seedDefaults} className="admin-btn-secondary">
                  <Sparkles size={14} /> Örnek dilimleri yükle
                </button>
              )}
            />
          ) : (
            <div className="wh-seg-list">
              {segments.map((segment, index) => {
                const chance = segmentChance(segment, stats.totalWeight);
                const game = games.find((entry) => entry.id === segment.game_id);
                const dead = segment.active && Number(segment.weight || 0) <= 0;
                return (
                  <div
                    key={segment.id}
                    className={`wh-seg${segment.active ? '' : ' is-off'}${dead ? ' is-dead' : ''}`}
                    onMouseEnter={() => setHighlight(segment.id)}
                    onMouseLeave={() => setHighlight('')}
                  >
                    <span
                      className="wh-seg-color"
                      style={{ background: segment.color, color: segment.text_color }}
                    >
                      {index + 1}
                    </span>

                    <div className="wh-seg-main">
                      <div className="wh-seg-label">{segment.label}</div>
                      <div className="wh-seg-sub">
                        {segment.active ? 'Çarkta görünür' : 'Pasif — çarkta çizilmez'}
                        {game ? ` · ${game.name}` : ''}
                      </div>
                    </div>

                    <div className="wh-seg-reward">
                      <span className={`badge-pill ${REWARD_BADGES[segment.reward_type] || 'badge-gray'}`}>
                        {segment.reward_type === 'freespin'
                          ? <Dices size={11} /> : <Coins size={11} />}
                        {REWARD_LABELS[segment.reward_type] || segment.reward_type}
                      </span>
                      <span className="wh-seg-amount">{rewardSummary(segment)}</span>
                    </div>

                    <div className="wh-seg-chance">
                      <div className="wh-chance-top">
                        <span>{`Ağırlık ${segment.weight}`}</span>
                        <b>{segment.active ? percent(chance) : '—'}</b>
                      </div>
                      <div className="wh-chance-bar">
                        <div
                          className="wh-chance-fill"
                          style={{ width: `${Math.round(chance * 100)}%` }}
                        />
                      </div>
                    </div>

                    <div className="wh-seg-actions">
                      <div className="wh-order-stack">
                        <button
                          type="button"
                          className="wh-order-btn"
                          disabled={index === 0 || ordering}
                          onClick={() => moveSegment(index, -1)}
                          title="Yukarı taşı"
                          aria-label={`${segment.label} dilimini yukarı taşı`}
                        >
                          <ChevronUp size={11} />
                        </button>
                        <button
                          type="button"
                          className="wh-order-btn"
                          disabled={index === segments.length - 1 || ordering}
                          onClick={() => moveSegment(index, 1)}
                          title="Aşağı taşı"
                          aria-label={`${segment.label} dilimini aşağı taşı`}
                        >
                          <ChevronDown size={11} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => openEdit(segment)}
                        className="icon-btn edit"
                        title="Düzenle"
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleSegment(segment)}
                        className="icon-btn view"
                        title={segment.active ? 'Pasife al' : 'Aktifleştir'}
                      >
                        {segment.active ? <EyeOff size={13} /> : <Eye size={13} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirm(segment)}
                        className="icon-btn ban"
                        title="Sil"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      ) : (
        <section className="wh-card">
          <div className="wh-spin-bar">
            <div className="wh-search">
              <Search size={14} />
              <input
                value={spinSearch}
                onChange={(event) => onSearch(event.target.value)}
                className="admin-input"
                placeholder="Kullanıcı adına göre ara"
                aria-label="Kullanıcı adına göre ara"
              />
            </div>
            {spinsBusy && <Loader2 size={15} className="animate-spin" />}
            <div className="wh-spin-total">
              {/* Uç yalnızca **bu sayfadaki** satırların toplamını döndürür;
                  "tüm zamanların toplamı" diye yazmak yanlış olurdu. */}
              Bu sayfadaki ödül toplamı: <b>{money(spins.total_amount)}</b>
            </div>
          </div>

          {spins.items.length === 0 ? (
            <AdminEmpty
              title="Çevirme kaydı yok"
              description={spinSearch.trim()
                ? 'Bu kullanıcı adına uyan bir çevirme bulunamadı.'
                : 'Üyeler çarkı çevirdikçe kayıtlar burada listelenir.'}
            />
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table w-full min-w-[760px]">
                  <thead>
                    <tr>
                      <th>Tarih</th><th>Kullanıcı</th><th>Dilim</th>
                      <th>Ödül</th><th>Çevrim</th><th>Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {spins.items.map((spin) => (
                      <tr key={spin.id}>
                        <td className="text-slate-400">{formatDate(spin.created_at)}</td>
                        <td className="font-semibold">{spin.username}</td>
                        <td>{spin.segment_label}</td>
                        <td>
                          {spin.reward_type === 'none' ? '—'
                            : spin.reward_type === 'freespin'
                              ? `${spin.spin_count} freespin`
                              : money(spin.amount)}
                        </td>
                        <td className="text-slate-400">
                          {Number(spin.wager_required || 0) > 0
                            ? money(spin.wager_required) : '—'}
                        </td>
                        <td>
                          <span className={`badge-pill ${
                            spin.status === 'completed' ? 'badge-green' : 'badge-yellow'
                          }`}
                          >
                            {spin.status === 'completed' ? 'Tamamlandı'
                              : spin.status === 'pending' ? 'Sağlayıcı bekleniyor'
                                : spin.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 && (
                <div className="wh-pager">
                  <button
                    type="button"
                    disabled={spinPage === 0}
                    onClick={() => goPage(spinPage - 1)}
                  >
                    Önceki
                  </button>
                  {pageWindow(spinPage, pageCount).map((entry, index) => (
                    entry === 'gap' ? (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={`gap-${index}`}>…</span>
                    ) : (
                      <button
                        key={entry}
                        type="button"
                        className={entry === spinPage ? 'is-active' : ''}
                        onClick={() => goPage(entry)}
                      >
                        {entry + 1}
                      </button>
                    )
                  ))}
                  <button
                    type="button"
                    disabled={spinPage >= pageCount - 1}
                    onClick={() => goPage(spinPage + 1)}
                  >
                    Sonraki
                  </button>
                </div>
              )}
              <div className="wh-pager">
                <PageSizeSelect
                  value={spinSize}
                  disabled={spinsBusy}
                  onChange={(size) => {
                    setSpinSize(size);
                    writePageSize('wheel-spins', size);
                    setSpinPage(0);
                    loadSpins({ username: spinSearch, page: 0, limit: size });
                  }}
                />
              </div>
            </>
          )}
        </section>
      )}

      {modal && (
        <div className="wh-dialog-layer" role="presentation">
          <button
            type="button"
            className="wh-dialog-backdrop"
            aria-label="Pencereyi kapat"
            onClick={() => setModal(null)}
          />
          <section className="wh-dialog" role="dialog" aria-modal="true">
            <div className="wh-dialog-head">
              <div>
                <h2>{modal.mode === 'new' ? 'Yeni Dilim' : 'Dilimi Düzenle'}</h2>
                <p>Ödülü, kazanma ağırlığını ve rengini belirleyin</p>
              </div>
              <button
                type="button"
                onClick={() => setModal(null)}
                className="icon-btn view ml-auto"
                aria-label="Kapat"
              >
                <X size={14} />
              </button>
            </div>

            <div className="wh-dialog-body">
              <div className="wh-fields">
                <div
                  className="wh-chip-preview"
                  style={{ background: form.color, color: form.text_color }}
                >
                  {form.label.trim() || 'Dilim yazısı'}
                </div>

                <Field label="Dilim Yazısı">
                  <input
                    value={form.label}
                    onChange={(event) => setForm({ ...form, label: event.target.value })}
                    className="admin-input"
                    placeholder="50 ₺ Bonus"
                    maxLength={64}
                  />
                </Field>
                <Field label="Ödül Türü">
                  <select
                    value={form.reward_type}
                    onChange={(event) => setForm({
                      ...form, reward_type: event.target.value,
                    })}
                    className="admin-input"
                  >
                    <option value="cash">Bakiye (çevrimsiz, doğrudan yüklenir)</option>
                    <option value="bonus">Bonus Bakiye (çevrim şartlı)</option>
                    <option value="freespin">Freespin</option>
                    <option value="none">Boş Dilim</option>
                  </select>
                </Field>

                {form.reward_type !== 'none' && (
                  <Field label={form.reward_type === 'freespin'
                    ? 'Spin Bahis Tutarı' : 'Ödül Tutarı (₺)'}
                  >
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.amount}
                      onChange={(event) => setForm({ ...form, amount: event.target.value })}
                      className="admin-input"
                    />
                  </Field>
                )}

                {form.reward_type === 'bonus' && (
                  <Field label="Çevrim Şartı (x)">
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={form.wager_multiplier}
                      onChange={(event) => setForm({
                        ...form, wager_multiplier: event.target.value,
                      })}
                      className="admin-input"
                    />
                  </Field>
                )}

                {form.reward_type === 'freespin' && (
                  <>
                    <Field label="Spin Sayısı">
                      <input
                        type="number"
                        min="1"
                        value={form.spin_count}
                        onChange={(event) => setForm({
                          ...form, spin_count: event.target.value,
                        })}
                        className="admin-input"
                      />
                    </Field>
                    <Field label="İlişkili Oyun" wide>
                      {freespinGames.length > 0 ? (
                        <select
                          value={form.game_id}
                          onChange={(event) => setForm({
                            ...form, game_id: event.target.value,
                          })}
                          className="admin-input"
                        >
                          <option value="">Oyun seçin</option>
                          {freespinGames.map((game) => (
                            <option key={game.id} value={game.id}>
                              {game.name} · {game.provider} ({game.reel_provider_name || game.api_provider})
                            </option>
                          ))}
                        </select>
                      ) : (
                        // Freespin Gold Palace (freeround) veya ScorpioPlay
                        // (bonus-call) ile verilir; ikisi de kapalıyken boş
                        // liste yerine nedenini yazıyoruz.
                        <div className="wh-note is-warn">
                          <AlertTriangle size={15} />
                          <div>
                            Freespin dilimi Gold Palace veya ScorpioPlay
                            entegrasyonunu gerektirir; şu anda aktif bir freespin
                            oyunu yok. Bu servislerden birini Oyun API
                            ayarlarından yapılandırıp açmadan freespin dilimi
                            tanımlanamaz.
                          </div>
                        </div>
                      )}
                    </Field>
                  </>
                )}

                <Field label="Kazanma Ağırlığı (0 = hiç gelmez)">
                  <input
                    type="number"
                    min="0"
                    value={form.weight}
                    onChange={(event) => setForm({ ...form, weight: event.target.value })}
                    className="admin-input"
                  />
                </Field>
                <Field label="Çarktaki Sırası">
                  <input
                    type="number"
                    min="0"
                    value={form.order}
                    onChange={(event) => setForm({ ...form, order: event.target.value })}
                    className="admin-input"
                  />
                </Field>

                <Field label="Dilim Rengi" wide>
                  <div className="wh-swatches">
                    {PALETTE.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => pickColor(color)}
                        aria-label={`Renk ${color}`}
                        className={`wh-swatch${form.color === color ? ' is-active' : ''}`}
                        style={{ background: color }}
                      />
                    ))}
                    <input
                      type="color"
                      value={form.color}
                      onChange={(event) => pickColor(event.target.value)}
                      className="wh-color-input"
                      aria-label="Özel renk"
                    />
                    <select
                      value={form.text_color}
                      onChange={(event) => setForm({
                        ...form, text_color: event.target.value,
                      })}
                      className="admin-input admin-input-w-sm"
                      aria-label="Yazı rengi"
                    >
                      <option value="#0b1220">Koyu yazı</option>
                      <option value="#ffffff">Açık yazı</option>
                    </select>
                  </div>
                </Field>

                <label className="wh-check">
                  <input
                    type="checkbox"
                    checked={form.active}
                    onChange={(event) => setForm({ ...form, active: event.target.checked })}
                  />
                  <div>
                    Aktif
                    <span>İşaretli değilse dilim çarkta hiç çizilmez.</span>
                  </div>
                </label>

                <div className="wh-dialog-actions">
                  <button
                    type="button"
                    onClick={() => setModal(null)}
                    className="admin-btn-secondary"
                  >
                    Vazgeç
                  </button>
                  <button
                    type="button"
                    disabled={busy || !form.label.trim()}
                    onClick={saveSegment}
                    className="admin-btn-primary"
                  >
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Kaydet
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(confirm)}
        title="Dilim silinsin mi?"
        description={confirm
          ? `"${confirm.label}" dilimi kalıcı olarak silinecek. Ağırlıklı dilim `
            + 'kalmazsa çark otomatik olarak kapatılır.'
          : ''}
        confirmLabel="Sil"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => removeSegment(confirm)}
      />
    </div>
  );
};

export default WheelSettings;
